import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faBox, 
    faPlus, 
    faSearch, 
    faFilter, 
    faArrowTrendDown, 
    faArrowTrendUp,
    faTrashCan,
    faPenToSquare,
    faTriangleExclamation
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import Sidebar from '../Sidebar/Sidebar';
import { buildApiUrl } from '../../config/api.config';

const InventoryDashboard = () => {
    const [inventory, setInventory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('All');
    
    // Form and Modal State
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentItemId, setCurrentItemId] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        quantity: 0,
        unit: 'kg',
        minStockLevel: 10,
        category: 'General',
        supplierName: '',
        supplierContact: ''
    });

    const categories = ['All', 'Vegetables', 'Meat', 'Dairy', 'Spices', 'Grains', 'Beverages', 'General'];

    const getRestaurantId = () => {
        const user = JSON.parse(localStorage.getItem("userDetails"));
        const selectedRestId = localStorage.getItem("currentRestaurantId");
        let activeRestaurantId = user.role === 'owner' ? selectedRestId : user.restaurantId;
        if (activeRestaurantId && typeof activeRestaurantId === 'object') {
            activeRestaurantId = activeRestaurantId._id;
        }
        return activeRestaurantId;
    };

    const fetchInventory = async () => {
        try {
            setLoading(true);
            const resId = getRestaurantId();
            const response = await axios.get(buildApiUrl(`/inventory/${resId}`));
            if (response.data.success) {
                setInventory(response.data.items);
            }
        } catch (error) {
            console.error("Fetch Inventory Error:", error);
            toast.error("Failed to load inventory");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInventory();
    }, []);

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const resId = getRestaurantId();
            const payload = {
                ...formData,
                supplier: {
                    name: formData.supplierName,
                    contact: formData.supplierContact
                },
                restaurantId: resId
            };

            if (isEditing) {
                await axios.put(buildApiUrl(`/inventory/update/${currentItemId}`), payload);
                toast.success("Item updated");
            } else {
                await axios.post(buildApiUrl('/inventory/add'), payload);
                toast.success("Item added to inventory");
            }

            setShowModal(false);
            resetForm();
            fetchInventory();
        } catch (error) {
            toast.error(error.response?.data?.message || "Operation failed");
        }
    };

    const handleEdit = (item) => {
        setIsEditing(true);
        setCurrentItemId(item._id);
        setFormData({
            name: item.name,
            quantity: item.quantity,
            unit: item.unit,
            minStockLevel: item.minStockLevel,
            category: item.category,
            supplierName: item.supplier?.name || '',
            supplierContact: item.supplier?.contact || ''
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this item?")) return;
        try {
            await axios.delete(buildApiUrl(`/inventory/delete/${id}`));
            toast.success("Item removed");
            fetchInventory();
        } catch (error) {
            toast.error("Delete failed");
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            quantity: 0,
            unit: 'kg',
            minStockLevel: 10,
            category: 'General',
            supplierName: '',
            supplierContact: ''
        });
        setIsEditing(false);
        setCurrentItemId(null);
    };

    const filteredInventory = inventory.filter(item => {
        const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = filterCategory === 'All' || item.category === filterCategory;
        return matchesSearch && matchesCategory;
    });

    const lowStockCount = inventory.filter(item => item.quantity <= item.minStockLevel).length;

    return (
        <div className="admin-layout" style={{ background: '#101113' }}>
            <Sidebar />
            <main style={{ padding: '3rem', overflowY: 'auto', background: '#101113' }}>
                <div style={{ width: '100%' }}>
                    
                    {/* Header System */}
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start',
                        marginBottom: '3rem',
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        paddingBottom: '1.5rem'
                    }}>
                        <div>
                            <h1 style={{ fontSize: '2.2rem', fontWeight: '900', margin: 0, color: 'white' }}>Inventory Ecosystem</h1>
                            <p style={{ color: 'rgba(255,255,255,0.4)', marginTop: '0.4rem', fontSize: '0.95rem', fontWeight: '500' }}>Manage raw materials, stock levels, and supply chain</p>
                        </div>
                        <button 
                            onClick={() => { resetForm(); setShowModal(true); }}
                            className="tf-btn tf-btn-primary"
                            style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '0.8rem', 
                                padding: '0.9rem 1.8rem',
                                borderRadius: '12px',
                                fontSize: '0.9rem',
                                fontWeight: '800'
                            }}
                        >
                            <FontAwesomeIcon icon={faPlus} /> Add New Entry
                        </button>
                    </div>

                    {/* Quick Stats */}
                    <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                        gap: '2rem',
                        marginBottom: '3rem'
                    }}>
                        <div style={{ background: '#151618', padding: '2rem', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.05)', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem', alignItems: 'center' }}>
                                <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px' }}>Total Stock Entries</span>
                                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255, 65, 53, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <FontAwesomeIcon icon={faBox} style={{ color: 'var(--tf-primary)', fontSize: '1rem' }} />
                                </div>
                            </div>
                            <div style={{ fontSize: '2.5rem', fontWeight: '900' }}>{inventory.length}</div>
                        </div>
                        <div style={{ 
                            background: lowStockCount > 0 ? 'rgba(255, 65, 53, 0.03)' : '#151618', 
                            padding: '2rem', 
                            borderRadius: '20px', 
                            border: lowStockCount > 0 ? '1px solid rgba(255, 65, 53, 0.15)' : '1px solid rgba(255,255,255,0.05)',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem', alignItems: 'center' }}>
                                <span style={{ color: lowStockCount > 0 ? '#ff4135' : 'rgba(255,255,255,0.4)', fontSize: '0.75rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px' }}>Low Stock Alerts</span>
                                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255, 65, 53, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: '#ff4135', fontSize: '1rem' }} />
                                </div>
                            </div>
                            <div style={{ fontSize: '2.5rem', fontWeight: '900', color: lowStockCount > 0 ? '#ff4135' : 'white' }}>{lowStockCount}</div>
                        </div>
                    </div>

                    {/* Filter & Search Bar */}
                    <div style={{ 
                        display: 'flex', 
                        gap: '2rem', 
                        background: '#151618', 
                        padding: '1.5rem', 
                        borderRadius: '20px',
                        border: '1px solid rgba(255,255,255,0.05)',
                        marginBottom: '3rem',
                        alignItems: 'center',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                    }}>
                        <div style={{ position: 'relative', flex: 1 }}>
                            <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: '1.2rem', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.2)' }} />
                            <input 
                                type="text" 
                                placeholder="Search by item name..." 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{ 
                                    width: '100%', 
                                    background: 'rgba(255,255,255,0.02)', 
                                    border: '1px solid rgba(255,255,255,0.08)',
                                    borderRadius: '12px',
                                    padding: '1rem 1rem 1rem 3.2rem',
                                    color: 'white',
                                    outline: 'none',
                                    fontSize: '0.95rem',
                                    transition: 'border-color 0.2s'
                                }}
                                onFocus={(e) => e.target.style.borderColor = 'var(--tf-primary)'}
                                onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                            <div style={{ fontSize: '0.75rem', fontWeight: '800', color: 'rgba(255,255,255,0.2)', textTransform: 'uppercase', letterSpacing: '1px' }}>Quick Filter</div>
                            <div style={{ display: 'flex', gap: '0.7rem' }}>
                                {categories.slice(0, 6).map(cat => (
                                    <button 
                                        key={cat}
                                        onClick={() => setFilterCategory(cat)}
                                        style={{ 
                                            padding: '0.6rem 1.2rem',
                                            borderRadius: '10px',
                                            fontSize: '0.8rem',
                                            fontWeight: '700',
                                            background: filterCategory === cat ? 'var(--tf-primary)' : 'rgba(255,255,255,0.03)',
                                            color: filterCategory === cat ? 'white' : 'rgba(255,255,255,0.4)',
                                            border: '1px solid',
                                            borderColor: filterCategory === cat ? 'var(--tf-primary)' : 'rgba(255,255,255,0.05)',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        {cat}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Inventory List */}
                    <div style={{ background: '#151618', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.05)', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'rgba(255,255,255,0.01)' }}>
                                    <th style={{ padding: '1.5rem', color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: '900' }}>Item Analytics</th>
                                    <th style={{ padding: '1.5rem', color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: '900' }}>Classification</th>
                                    <th style={{ padding: '1.5rem', color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: '900' }}>Stock Metric</th>
                                    <th style={{ padding: '1.5rem', color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: '900' }}>Node Status</th>
                                    <th style={{ padding: '1.5rem', color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: '900', textAlign: 'right' }}>Operations</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="5" style={{ textAlign: 'center', padding: '6rem', color: 'rgba(255,255,255,0.2)', fontSize: '1rem', fontWeight: '600' }}>Scanning Inventory Nodes...</td></tr>
                                ) : filteredInventory.length === 0 ? (
                                    <tr><td colSpan="5" style={{ textAlign: 'center', padding: '6rem', color: 'rgba(255,255,255,0.2)', fontSize: '1rem', fontWeight: '600' }}>No active inventory records found.</td></tr>
                                ) : (
                                    filteredInventory.map(item => (
                                        <tr key={item._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)', transition: 'background 0.2s' }} className="hover-row">
                                            <td style={{ padding: '1.5rem' }}>
                                                <div style={{ fontWeight: '800', fontSize: '1.1rem', color: 'white' }}>{item.name}</div>
                                                <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)', marginTop: '0.3rem', fontWeight: '600' }}>SUPPLIER: {item.supplier?.name || 'GENERIC VENDOR'}</div>
                                            </td>
                                            <td style={{ padding: '1.5rem' }}>
                                                <span style={{ 
                                                    padding: '6px 14px', 
                                                    borderRadius: '8px', 
                                                    background: 'rgba(255,255,255,0.03)', 
                                                    fontSize: '0.75rem', 
                                                    color: 'rgba(255,255,255,0.5)',
                                                    fontWeight: '800',
                                                    border: '1px solid rgba(255,255,255,0.05)'
                                                }}>{item.category}</span>
                                            </td>
                                            <td style={{ padding: '1.5rem' }}>
                                                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem' }}>
                                                    <span style={{ fontSize: '1.4rem', fontWeight: '900', color: 'white' }}>{item.quantity}</span>
                                                    <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.3)', fontWeight: '800' }}>{item.unit}</span>
                                                </div>
                                            </td>
                                            <td style={{ padding: '1.5rem' }}>
                                                {item.quantity <= item.minStockLevel ? (
                                                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.6rem', color: '#ff4135', fontSize: '0.75rem', fontWeight: '900', background: 'rgba(255, 65, 53, 0.08)', padding: '6px 14px', borderRadius: '10px', border: '1px solid rgba(255, 65, 53, 0.1)' }}>
                                                        <FontAwesomeIcon icon={faArrowTrendDown} /> CRITICAL STOCK
                                                    </div>
                                                ) : (
                                                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.6rem', color: '#00d1b2', fontSize: '0.75rem', fontWeight: '900', background: 'rgba(0, 209, 178, 0.08)', padding: '6px 14px', borderRadius: '10px', border: '1px solid rgba(0, 209, 178, 0.1)' }}>
                                                        <FontAwesomeIcon icon={faArrowTrendUp} /> OPTIMAL
                                                    </div>
                                                )}
                                            </td>
                                            <td style={{ padding: '1.5rem', textAlign: 'right' }}>
                                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1.2rem' }}>
                                                    <button onClick={() => handleEdit(item)} style={{ width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', transition: 'all 0.2s' }} onMouseOver={e => {e.currentTarget.style.color='white'; e.currentTarget.style.background='rgba(255,255,255,0.08)'}} onMouseOut={e => {e.currentTarget.style.color='rgba(255,255,255,0.4)'; e.currentTarget.style.background='rgba(255,255,255,0.03)'}}>
                                                        <FontAwesomeIcon icon={faPenToSquare} />
                                                    </button>
                                                    <button onClick={() => handleDelete(item._id)} style={{ width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255, 65, 53, 0.05)', border: '1px solid rgba(255, 65, 53, 0.1)', color: 'rgba(255, 65, 53, 0.5)', cursor: 'pointer', transition: 'all 0.2s' }} onMouseOver={e => {e.currentTarget.style.color='#ff4135'; e.currentTarget.style.background='rgba(255, 65, 53, 0.1)'}} onMouseOut={e => {e.currentTarget.style.color='rgba(255, 65, 53, 0.5)'; e.currentTarget.style.background='rgba(255, 65, 53, 0.05)'}}>
                                                        <FontAwesomeIcon icon={faTrashCan} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>

            {/* Entry/Edit Modal */}
            {showModal && (
                <div style={{ 
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', 
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
                    backdropFilter: 'blur(8px)'
                }}>
                    <div style={{ 
                        background: '#1a1b1e', width: '100%', maxWidth: '550px', 
                        borderRadius: '24px', border: '1px solid rgba(255,255,255,0.1)',
                        padding: '2.5rem', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)'
                    }}>
                        <h2 style={{ fontSize: '1.5rem', fontWeight: '800', marginBottom: '2rem' }}>
                            {isEditing ? 'Modify Inventory Node' : 'Initialize New Inventory Entry'}
                        </h2>
                        
                        <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '1.5rem' }}>
                            <div className="tf-grid">
                                <label style={{ fontSize: '0.75rem', fontWeight: '800', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase' }}>Item Manifest Name</label>
                                <input name="name" value={formData.name} onChange={handleFormChange} required className="tf-input" placeholder="e.g. Organic Tomatoes" />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                                <div className="tf-grid">
                                    <label style={{ fontSize: '0.75rem', fontWeight: '800', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase' }}>Available Nodes (Stock)</label>
                                    <input type="number" name="quantity" value={formData.quantity} onChange={handleFormChange} required className="tf-input" />
                                </div>
                                <div className="tf-grid">
                                    <label style={{ fontSize: '0.75rem', fontWeight: '800', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase' }}>Measurement Unit</label>
                                    <select name="unit" value={formData.unit} onChange={handleFormChange} className="tf-input">
                                        <option value="kg">kilograms (kg)</option>
                                        <option value="liters">liters (L)</option>
                                        <option value="units">units (qty)</option>
                                        <option value="boxes">boxes</option>
                                        <option value="grams">grams (g)</option>
                                    </select>
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                                <div className="tf-grid">
                                    <label style={{ fontSize: '0.75rem', fontWeight: '800', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase' }}>Min. Buffer Level</label>
                                    <input type="number" name="minStockLevel" value={formData.minStockLevel} onChange={handleFormChange} className="tf-input" />
                                </div>
                                <div className="tf-grid">
                                    <label style={{ fontSize: '0.75rem', fontWeight: '800', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase' }}>Classification</label>
                                    <select name="category" value={formData.category} onChange={handleFormChange} className="tf-input">
                                        {categories.filter(c => c !== 'All').map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div style={{ padding: '1.5rem', background: 'rgba(255,255,255,0.02)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                <label style={{ fontSize: '0.7rem', fontWeight: '900', color: 'var(--tf-primary)', textTransform: 'uppercase', display: 'block', marginBottom: '1rem' }}>Supplier Logistics</label>
                                <div style={{ display: 'grid', gap: '1rem' }}>
                                    <input name="supplierName" value={formData.supplierName} onChange={handleFormChange} className="tf-input" placeholder="Vendor Name" style={{ background: '#151618' }} />
                                    <input name="supplierContact" value={formData.supplierContact} onChange={handleFormChange} className="tf-input" placeholder="Contact Channel" style={{ background: '#151618' }} />
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                                <button type="button" onClick={() => setShowModal(false)} className="tf-btn" style={{ flex: 1, background: 'rgba(255,255,255,0.05)', color: 'white' }}>Cancel Bypass</button>
                                <button type="submit" className="tf-btn tf-btn-primary" style={{ flex: 1.5 }}>{isEditing ? 'Commit Changes' : 'Initialize Item'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style>{`
                .hover-row:hover {
                    background: rgba(255,255,255,0.02);
                }
            `}</style>
        </div>
    );
};

export default InventoryDashboard;
